/**
 * CRUD for List Builder: cities and items (activities, meals, hotels).
 * localStorage is the primary store; writes are also pushed to the backend.
 */

import { apiPut, apiGet, getUserEmailFromToken } from './apiClient';
import type {
  ListBuilderCity,
  ListBuilderActivity,
  ListBuilderMeal,
  ListBuilderHotel,
} from '../types/listBuilder';

const STORAGE_PREFIX = 'wt_listbuilder_';

export interface ListBuilderData {
  cities: ListBuilderCity[];
  activities: ListBuilderActivity[];
  meals: ListBuilderMeal[];
  hotels: ListBuilderHotel[];
}

function getStorageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function migrateAddress(raw: unknown): ListBuilderActivity['address'] {
  if (!raw || typeof raw !== 'object') return null;
  const addr = raw as Record<string, unknown>;
  return {
    line1: addr.line1 != null ? String(addr.line1) : null,
    line2: addr.line2 != null ? String(addr.line2) : null,
    city: addr.city != null ? String(addr.city) : null,
    state: addr.state != null ? String(addr.state) : null,
    postalCode: addr.postalCode != null ? String(addr.postalCode) : null,
    country: addr.country != null ? String(addr.country) : null,
  };
}

function migrateActivity(a: ListBuilderActivity | Record<string, unknown>): ListBuilderActivity {
  const raw = a as Record<string, unknown>;
  const hasLegacyName = 'name' in raw && !('title' in raw);
  return {
    id: String(raw.id ?? crypto.randomUUID()),
    cityId: String(raw.cityId ?? ''),
    title: hasLegacyName ? String(raw.name ?? '') : String(raw.title ?? ''),
    description: raw.description != null ? String(raw.description) : null,
    operatingHours: raw.operatingHours != null ? String(raw.operatingHours) : null,
    recommendedTimes: Array.isArray(raw.recommendedTimes) ? raw.recommendedTimes.filter((x): x is string => typeof x === 'string') : [],
    bestTimeReason: raw.bestTimeReason != null ? String(raw.bestTimeReason) : null,
    area: raw.area != null ? String(raw.area) : null,
    url: raw.url != null ? String(raw.url) : (raw.link != null ? String(raw.link) : null),
    suggestedDurationMinutes: typeof raw.suggestedDurationMinutes === 'number' ? raw.suggestedDurationMinutes : undefined,
    transit: ['walk', 'metro', 'tram', 'bus', 'rideshare', 'bike'].includes(String(raw.transit)) ? raw.transit as ListBuilderActivity['transit'] : undefined,
    kidFriendly: typeof raw.kidFriendly === 'boolean' ? raw.kidFriendly : undefined,
    accessibilityNotes: raw.accessibilityNotes != null ? String(raw.accessibilityNotes) : null,
    priceCategory: ['free', '$', '$$', '$$$', '$$$$'].includes(String(raw.priceCategory)) ? raw.priceCategory as ListBuilderActivity['priceCategory'] : undefined,
    tags: Array.isArray(raw.tags) ? raw.tags.filter((x): x is string => typeof x === 'string') : [],
    recommendedIfStayAtLeastDays: typeof raw.recommendedIfStayAtLeastDays === 'number' ? raw.recommendedIfStayAtLeastDays : undefined,
    geocode: raw.geocode && typeof raw.geocode === 'object' && typeof (raw.geocode as { latitude?: unknown }).latitude === 'number' && typeof (raw.geocode as { longitude?: unknown }).longitude === 'number'
      ? { latitude: (raw.geocode as { latitude: number }).latitude, longitude: (raw.geocode as { longitude: number }).longitude }
      : null,
    address: migrateAddress(raw.address),
    phoneNumber: raw.phoneNumber != null ? String(raw.phoneNumber) : null,
  };
}

function migrateMeal(m: ListBuilderMeal | Record<string, unknown>): ListBuilderMeal {
  const raw = m as Record<string, unknown>;
  return {
    id: String(raw.id ?? crypto.randomUUID()),
    cityId: String(raw.cityId ?? ''),
    name: String(raw.name ?? ''),
    cuisine: raw.cuisine != null ? String(raw.cuisine) : null,
    description: raw.description != null ? String(raw.description) : null,
    operatingHours: raw.operatingHours != null ? String(raw.operatingHours) : null,
    priceCategory: ['free', '$', '$$', '$$$', '$$$$'].includes(String(raw.priceCategory)) ? raw.priceCategory as ListBuilderMeal['priceCategory'] : undefined,
    area: raw.area != null ? String(raw.area) : null,
    url: raw.url != null ? String(raw.url) : (raw.link != null ? String(raw.link) : null),
    matchScore: typeof raw.matchScore === 'number' ? raw.matchScore : undefined,
    reasoning: raw.reasoning != null ? String(raw.reasoning) : null,
    reservationRequirement: ['required', 'recommended', 'not_needed'].includes(String(raw.reservationRequirement)) ? raw.reservationRequirement as ListBuilderMeal['reservationRequirement'] : undefined,
    kidFriendly: typeof raw.kidFriendly === 'boolean' ? raw.kidFriendly : undefined,
    tags: Array.isArray(raw.tags) ? raw.tags.filter((x): x is string => typeof x === 'string') : [],
    geocode: raw.geocode && typeof raw.geocode === 'object' && typeof (raw.geocode as { latitude?: unknown }).latitude === 'number' && typeof (raw.geocode as { longitude?: unknown }).longitude === 'number'
      ? { latitude: (raw.geocode as { latitude: number }).latitude, longitude: (raw.geocode as { longitude: number }).longitude }
      : null,
    address: migrateAddress(raw.address),
    phoneNumber: raw.phoneNumber != null ? String(raw.phoneNumber) : null,
  };
}

function migrateHotel(h: ListBuilderHotel | Record<string, unknown>): ListBuilderHotel {
  const raw = h as Record<string, unknown>;
  return {
    id: String(raw.id ?? crypto.randomUUID()),
    cityId: String(raw.cityId ?? ''),
    name: String(raw.name ?? ''),
    description: raw.description != null ? String(raw.description) : null,
    budgetCategory: raw.budgetCategory != null ? String(raw.budgetCategory) : null,
    pricePerNight: typeof raw.pricePerNight === 'number' ? raw.pricePerNight : undefined,
    currency: raw.currency != null ? String(raw.currency) : null,
    area: raw.area != null ? String(raw.area) : (raw.neighborhood != null ? String(raw.neighborhood) : null),
    url: raw.url != null ? String(raw.url) : (raw.link != null ? String(raw.link) : null),
    amenities: Array.isArray(raw.amenities) ? raw.amenities.filter((x): x is string => typeof x === 'string') : [],
    rating: typeof raw.rating === 'number' ? raw.rating : undefined,
    familyNotes: raw.familyNotes != null ? String(raw.familyNotes) : null,
    accessibilityNotes: raw.accessibilityNotes != null ? String(raw.accessibilityNotes) : null,
    tags: Array.isArray(raw.tags) ? raw.tags.filter((x): x is string => typeof x === 'string') : [],
    geocode: raw.geocode && typeof raw.geocode === 'object' && typeof (raw.geocode as { latitude?: unknown }).latitude === 'number' && typeof (raw.geocode as { longitude?: unknown }).longitude === 'number'
      ? { latitude: (raw.geocode as { latitude: number }).latitude, longitude: (raw.geocode as { longitude: number }).longitude }
      : null,
    address: migrateAddress(raw.address),
    phoneNumber: raw.phoneNumber != null ? String(raw.phoneNumber) : null,
  };
}

function migrateCity(c: ListBuilderCity | Record<string, unknown>): ListBuilderCity {
  const raw = c as Record<string, unknown>;
  const historyObj = raw.history && typeof raw.history === 'object' && !Array.isArray(raw.history)
    ? (raw.history as Record<string, unknown>)
    : null;
  const summary = typeof historyObj?.summary === 'string' ? historyObj.summary : '';
  return {
    id: String(raw.id ?? crypto.randomUUID()),
    name: String(raw.name ?? ''),
    country: String(raw.country ?? ''),
    region: raw.region != null ? String(raw.region) : undefined,
    notes: raw.notes != null ? String(raw.notes) : undefined,
    history: summary ? { summary } : undefined,
  };
}

function loadData(userId: string): ListBuilderData {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (!raw) return { cities: [], activities: [], meals: [], hotels: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return { cities: [], activities: [], meals: [], hotels: [] };
    }
    const cities = Array.isArray(parsed.cities) ? parsed.cities.map((x: unknown) => migrateCity(x as ListBuilderCity)) : [];
    const activities = Array.isArray(parsed.activities)
      ? parsed.activities.map((x: unknown) => migrateActivity(x as Record<string, unknown> | ListBuilderActivity))
      : [];
    const meals = Array.isArray(parsed.meals)
      ? parsed.meals.map((x: unknown) => migrateMeal(x as Record<string, unknown> | ListBuilderMeal))
      : [];
    const hotels = Array.isArray(parsed.hotels)
      ? parsed.hotels.map((x: unknown) => migrateHotel(x as Record<string, unknown> | ListBuilderHotel))
      : [];
    return {
      cities,
      activities,
      meals,
      hotels,
    };
  } catch {
    return { cities: [], activities: [], meals: [], hotels: [] };
  }
}

const BACKEND_KEY_PREFIX = 'wt_listbuilder';

function saveData(userId: string, data: ListBuilderData): void {
  localStorage.setItem(getStorageKey(userId), JSON.stringify(data));
  // Fire-and-forget push to backend
  pushToBackend(data);
}

/** Push list builder data to the backend. Fire-and-forget; errors are logged. */
function pushToBackend(data: ListBuilderData): void {
  const email = getUserEmailFromToken();
  if (!email) return;
  const key = `${BACKEND_KEY_PREFIX}-${email}`;
  apiPut(key, key, data).catch((err) => {
    console.warn('listBuilderService: backend push failed', err);
  });
}

/**
 * Push current localStorage list builder data to the backend.
 * Call on page mount to ensure existing data is synced.
 */
export function pushListBuilderToBackend(userId: string): void {
  const data = loadData(userId);
  const hasData = data.cities.length > 0 || data.activities.length > 0 || data.meals.length > 0 || data.hotels.length > 0;
  if (!hasData) return;
  pushToBackend(data);
}

/**
 * Pull list builder data from the backend and write to localStorage (no push).
 * Call on login so user continues where they left off.
 * If null or invalid, does not write.
 */
export async function pullListBuilderFromBackend(userId: string, email: string): Promise<void> {
  try {
    const key = `${BACKEND_KEY_PREFIX}-${email}`;
    const body = await apiGet<Record<string, unknown>>(key);
    if (!body || typeof body !== 'object' || Array.isArray(body)) return;
    const parsed = body as { cities?: unknown[]; activities?: unknown[]; meals?: unknown[]; hotels?: unknown[] };
    const cities = Array.isArray(parsed.cities) ? parsed.cities.map((x: unknown) => migrateCity(x as ListBuilderCity)) : [];
    const activities = Array.isArray(parsed.activities)
      ? parsed.activities.map((x) => migrateActivity(x as Record<string, unknown> | ListBuilderActivity))
      : [];
    const meals = Array.isArray(parsed.meals)
      ? parsed.meals.map((x) => migrateMeal(x as Record<string, unknown> | ListBuilderMeal))
      : [];
    const hotels = Array.isArray(parsed.hotels)
      ? parsed.hotels.map((x) => migrateHotel(x as Record<string, unknown> | ListBuilderHotel))
      : [];
    const data: ListBuilderData = { cities, activities, meals, hotels };
    const hasData = cities.length > 0 || activities.length > 0 || meals.length > 0 || hotels.length > 0;
    if (!hasData) return;
    localStorage.setItem(getStorageKey(userId), JSON.stringify(data));
  } catch (err) {
    console.warn('listBuilderService: pull from backend failed', err);
  }
}

// Cities
export function getAllCities(userId: string): ListBuilderCity[] {
  return loadData(userId).cities;
}

export function getCity(userId: string, cityId: string): ListBuilderCity | null {
  return loadData(userId).cities.find((c) => c.id === cityId) ?? null;
}

export function createCity(
  userId: string,
  data: Omit<ListBuilderCity, 'id'>
): ListBuilderCity {
  const d = loadData(userId);
  const city: ListBuilderCity = { ...data, id: crypto.randomUUID() };
  d.cities.push(city);
  saveData(userId, d);
  return city;
}

export function updateCity(
  userId: string,
  cityId: string,
  updates: Partial<Omit<ListBuilderCity, 'id'>>
): ListBuilderCity | null {
  const d = loadData(userId);
  const idx = d.cities.findIndex((c) => c.id === cityId);
  if (idx === -1) return null;
  d.cities[idx] = { ...d.cities[idx], ...updates };
  saveData(userId, d);
  return d.cities[idx];
}

export function deleteCity(userId: string, cityId: string): boolean {
  const d = loadData(userId);
  const filtered = d.cities.filter((c) => c.id !== cityId);
  if (filtered.length === d.cities.length) return false;
  d.cities = filtered;
  d.activities = d.activities.filter((a) => a.cityId !== cityId);
  d.meals = d.meals.filter((m) => m.cityId !== cityId);
  d.hotels = d.hotels.filter((h) => h.cityId !== cityId);
  saveData(userId, d);
  return true;
}

// Activities
export function getActivitiesByCity(userId: string, cityId: string): ListBuilderActivity[] {
  return loadData(userId).activities.filter((a) => a.cityId === cityId);
}

export function getAllActivities(userId: string): ListBuilderActivity[] {
  return loadData(userId).activities;
}

export function createActivity(
  userId: string,
  data: Omit<ListBuilderActivity, 'id'>
): ListBuilderActivity {
  const d = loadData(userId);
  const activity: ListBuilderActivity = { ...data, id: crypto.randomUUID() };
  d.activities.push(activity);
  saveData(userId, d);
  return activity;
}

export function updateActivity(
  userId: string,
  activityId: string,
  updates: Partial<Omit<ListBuilderActivity, 'id'>>
): ListBuilderActivity | null {
  const d = loadData(userId);
  const idx = d.activities.findIndex((a) => a.id === activityId);
  if (idx === -1) return null;
  d.activities[idx] = { ...d.activities[idx], ...updates };
  saveData(userId, d);
  return d.activities[idx];
}

export function deleteActivity(userId: string, activityId: string): boolean {
  const d = loadData(userId);
  const filtered = d.activities.filter((a) => a.id !== activityId);
  if (filtered.length === d.activities.length) return false;
  d.activities = filtered;
  saveData(userId, d);
  return true;
}

// Meals
export function getMealsByCity(userId: string, cityId: string): ListBuilderMeal[] {
  return loadData(userId).meals.filter((m) => m.cityId === cityId);
}

export function getAllMeals(userId: string): ListBuilderMeal[] {
  return loadData(userId).meals;
}

export function createMeal(userId: string, data: Omit<ListBuilderMeal, 'id'>): ListBuilderMeal {
  const d = loadData(userId);
  const meal: ListBuilderMeal = { ...data, id: crypto.randomUUID() };
  d.meals.push(meal);
  saveData(userId, d);
  return meal;
}

export function updateMeal(
  userId: string,
  mealId: string,
  updates: Partial<Omit<ListBuilderMeal, 'id'>>
): ListBuilderMeal | null {
  const d = loadData(userId);
  const idx = d.meals.findIndex((m) => m.id === mealId);
  if (idx === -1) return null;
  d.meals[idx] = { ...d.meals[idx], ...updates };
  saveData(userId, d);
  return d.meals[idx];
}

export function deleteMeal(userId: string, mealId: string): boolean {
  const d = loadData(userId);
  const filtered = d.meals.filter((m) => m.id !== mealId);
  if (filtered.length === d.meals.length) return false;
  d.meals = filtered;
  saveData(userId, d);
  return true;
}

// Hotels
export function getHotelsByCity(userId: string, cityId: string): ListBuilderHotel[] {
  return loadData(userId).hotels.filter((h) => h.cityId === cityId);
}

export function getAllHotels(userId: string): ListBuilderHotel[] {
  return loadData(userId).hotels;
}

export function createHotel(userId: string, data: Omit<ListBuilderHotel, 'id'>): ListBuilderHotel {
  const d = loadData(userId);
  const hotel: ListBuilderHotel = { ...data, id: crypto.randomUUID() };
  d.hotels.push(hotel);
  saveData(userId, d);
  return hotel;
}

export function updateHotel(
  userId: string,
  hotelId: string,
  updates: Partial<Omit<ListBuilderHotel, 'id'>>
): ListBuilderHotel | null {
  const d = loadData(userId);
  const idx = d.hotels.findIndex((h) => h.id === hotelId);
  if (idx === -1) return null;
  d.hotels[idx] = { ...d.hotels[idx], ...updates };
  saveData(userId, d);
  return d.hotels[idx];
}

export function deleteHotel(userId: string, hotelId: string): boolean {
  const d = loadData(userId);
  const filtered = d.hotels.filter((h) => h.id !== hotelId);
  if (filtered.length === d.hotels.length) return false;
  d.hotels = filtered;
  saveData(userId, d);
  return true;
}
